// src/pages/trading-portal/MarketDataIngestion/OptionsPayoffVisualizer.js
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Slider, CircularProgress
} from '@mui/material';
import axiosInstance from '../../../utils/axiosInstance';

const ACCENT = '#f59e0b';
const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const OptionsPayoffVisualizer = ({ contractId, currentPrice }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Controls
    const [iv, setIv] = useState(50); // 50%
    const [range, setRange] = useState(21); // +/- 21%

    useEffect(() => {
        if (!contractId) return;
        const fetchMatrix = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.post(
                    `/api/market-data-ingestion/options-chain/payoff-matrix/${contractId}`,
                    { implied_volatility: iv / 100, range_pct: range / 100 }
                );
                if (response.data.success) {
                    setData(response.data.data);
                }
            } catch (error) {
                console.error("Failed to fetch payoff matrix", error);
            } finally {
                setLoading(false);
            }
        };
        
        // Debounce API calls slightly when dragging sliders
        const timeoutId = setTimeout(() => fetchMatrix(), 300);
        return () => clearTimeout(timeoutId);
    }, [contractId, iv, range]);

    // Color gradient calculator (like OptionStrat)
    const getCellColor = (value) => {
        if (value === 0) return '#1e293b'; // neutral dark
        
        // Find intensity based on an arbitrary max visible P&L (e.g., $1000 bounds)
        const intensity = Math.min(Math.abs(value) / 800, 1); 
        
        if (value > 0) {
            // Greens
            return `rgba(16, 185, 129, ${0.2 + (intensity * 0.8)})`;
        } else {
            // Reds
            return `rgba(239, 68, 68, ${0.2 + (intensity * 0.8)})`;
        }
    };

    if (!contractId) return <Typography>Select a contract to view Payoff Matrix</Typography>;

    return (
        <Paper sx={{ p: 2, bgcolor: '#0f172a', color: 'white', borderRadius: 2 }}>
            {/* --- Top Summary Header --- */}
            <Grid container spacing={2} sx={{ mb: 3, textAlign: 'center', borderBottom: '1px solid #334155', pb: 2 }}>
                <Grid item xs={3}>
                    <Typography variant="caption" color="#94a3b8">NET DEBIT:</Typography>
                    <Typography fontWeight="bold" color="#fcd34d">
                        ${data?.summary?.net_debit || '---'}
                    </Typography>
                </Grid>
                <Grid item xs={3}>
                    <Typography variant="caption" color="#94a3b8">MAX LOSS:</Typography>
                    <Typography fontWeight="bold" color="#ef4444">
                        ${data?.summary?.max_loss || '---'}
                    </Typography>
                </Grid>
                <Grid item xs={3}>
                    <Typography variant="caption" color="#94a3b8">MAX PROFIT:</Typography>
                    <Typography fontWeight="bold" color="#10b981">
                        {data?.summary?.max_profit === 'Infinite' ? '↗ Infinite' : `$${data?.summary?.max_profit}`}
                    </Typography>
                </Grid>
                <Grid item xs={3}>
                    <Typography variant="caption" color="#94a3b8">BREAKEVEN:</Typography>
                    <Typography fontWeight="bold" color="#e2e8f0">
                        {data?.summary?.breakeven ? `Above $${data.summary.breakeven}` : '---'}
                    </Typography>
                </Grid>
            </Grid>

            {/* --- The Heatmap Table --- */}
            <Box sx={{ overflowX: 'auto', position: 'relative', minHeight: '300px' }}>
                {loading && (
                    <Box sx={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                        <CircularProgress sx={{ color: ACCENT }} />
                    </Box>
                )}
                
                <table style={{ width: '100%', borderCollapse: 'collapse', opacity: loading ? 0.3 : 1, transition: 'opacity 0.2s' }}>
                    <thead>
                        <tr>
                            <th style={{ padding: '8px', textAlign: 'left', minWidth: '80px' }}>Price</th>
                            {data?.dates.map((date, i) => (
                                <th key={i} style={{ padding: '8px', textAlign: 'center', fontSize: '13px', color: '#cbd5e1' }}>
                                    {date}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data?.prices.map((price, rowIndex) => (
                            <tr key={rowIndex}>
                                {/* Y-Axis Label: Underlying Price & Percent */}
                                <td style={{ padding: '4px 8px', fontSize: '12px', borderRight: '1px solid #334155', whiteSpace: 'nowrap' }}>
                                    <span style={{ fontWeight: 'bold', color: '#e2e8f0' }}>${price.toFixed(0)}</span>
                                    <span style={{ color: '#64748b', marginLeft: '6px' }}>{data.price_pcts[rowIndex]}%</span>
                                </td>
                                
                                {/* P&L Cells */}
                                {data.matrix[rowIndex].map((pnl, colIndex) => (
                                    <td key={colIndex} style={{
                                        padding: '6px 4px',
                                        textAlign: 'center',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        color: pnl >= 0 ? '#ecfdf5' : '#fef2f2',
                                        backgroundColor: getCellColor(pnl),
                                        border: '1px solid #1e293b'
                                    }}>
                                        ${Math.round(pnl)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Box>

            {/* --- Bottom Controls (Sliders) --- */}
            <Grid container spacing={4} sx={{ mt: 3, pt: 2, borderTop: '1px solid #334155' }}>
                <Grid item xs={6}>
                    <Typography variant="caption" color="#cbd5e1" gutterBottom>
                        RANGE: ±{range}%
                    </Typography>
                    <Slider
                        value={range}
                        min={5} max={50} step={1}
                        onChange={(e, val) => setRange(val)}
                        sx={{ color: '#94a3b8', '& .MuiSlider-thumb': { bgcolor: ACCENT } }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="caption" color="#cbd5e1" gutterBottom>
                        IMPLIED VOLATILITY: {iv}%
                    </Typography>
                    <Slider
                        value={iv}
                        min={10} max={200} step={1}
                        onChange={(e, val) => setIv(val)}
                        sx={{ color: '#94a3b8', '& .MuiSlider-thumb': { bgcolor: ACCENT } }}
                    />
                </Grid>
            </Grid>
        </Paper>
    );
};

export default OptionsPayoffVisualizer;